// React.
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Helpers.
import { updateExpiration } from './helpers/utils';
import ConditionalRender from "./helpers/ConditionalRender";
import archiver from "./api/Archiver";

// Types.

// Constants.
import {
	DATA_LOADED,
	DATA_LOADING, REDIRECT_PAGE, TOKEN_KEY, EXPIRATION_KEY,
} from './component/Constants';

// Views.
import Login from './views/login';
import Content from "./Content";
import ErrorPage from "./views/ErrorPage";

// Styles.
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min';
import 'highlight.js/styles/vs.css';
import 'html-query-plan/css/qp.css';
import 'primereact/resources/themes/bootstrap4-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './css/App.css';
import './css/html-query-plan-overrides.css';
import './css/primereact-overrides.css';
import './css/primereact-execution-plans.css';
import './css/tooltip.css';
import './fonts/mfizz/font-mfizz.css';
import './fonts/fontawesome/css/all.min.css';
import './fonts/fontawesome/css/custom-icons.min.css';
import 'highlight.js/styles/vs.css'

function App() {
    const [loading, setLoading] = useState<number>(DATA_LOADING);

    const [isLogged, setIsLogged] = useState(false);
    const [mustLogIn, setMustLogIn] = useState(false);
    const [loginError, setLoginError] = useState('');
    const [generalError, setGeneralError] = useState('');

    const navigate = useNavigate();
    const location = useLocation();

    function isAuthFailure(error: any): boolean {
		const status = error?.response?.status;
		if (status === 401 || status === 403) {
			return true;
		}

		return false;
    }

	useEffect(() => {
		const isLoginPage = window.location.pathname.startsWith('/login');
		const token = window.localStorage.getItem(TOKEN_KEY);
		if (!isLoginPage && token) {
			window.localStorage.setItem(REDIRECT_PAGE, window.location.href);
		}
	}, [location.pathname]);

	useEffect(() => {
		async function authenticate (token: string | null)
		{
			try
			{
				await archiver.get ('/auth.jsp');
				setIsLogged (true);
				setMustLogIn (!!token);
			}
			catch (error: any)
			{
				if (isAuthFailure(error))
				{
					// Invalid/expired/tampered credentials: force fresh login.
					window.localStorage.removeItem(TOKEN_KEY);
					window.localStorage.removeItem(EXPIRATION_KEY);
					setIsLogged (false);
					setGeneralError ('');
					setMustLogIn (true);
					setLoginError ('invalid');

					if (window.location.pathname !== '/login')
					{
						navigate ('/login');
					}
				}
				else
				{
					console.error ('error', error);
					setGeneralError (error.message);
                    navigate('/error');
				}
			}
		}

		async function initialiseAuth ()
		{
			const token = window.localStorage.getItem (TOKEN_KEY);
			const expiration = window.localStorage.getItem (EXPIRATION_KEY);

			if (!token || !expiration)
			{
				// Probe auth state when no local token:
				// - auth disabled => enter app directly
				// - auth enabled => route to login
				setLoginError ('');
				await authenticate (null);
			}
			else if (new Date () > new Date (expiration))
			{
				window.localStorage.removeItem(TOKEN_KEY);
				window.localStorage.removeItem(EXPIRATION_KEY);
				setIsLogged (false);
				setMustLogIn (true);
				setLoginError ('expired');
				navigate ('/login?error=expired');
			}
			else
			{
				updateExpiration ();
				await authenticate (token);
			}

			setLoading (DATA_LOADED);
		}

		void initialiseAuth ();
    }, [location.pathname]);

    return (
        <>
            <ConditionalRender if={generalError && loading === DATA_LOADED}>
				<ErrorPage message={generalError}/>
            </ConditionalRender>
            <ConditionalRender if={!generalError && mustLogIn && !isLogged && loading === DATA_LOADED}>
                <Login loginError={loginError}/>
            </ConditionalRender>
            <ConditionalRender if={!generalError && isLogged && loading === DATA_LOADED}>
                <Content loginRequired={mustLogIn}/>
            </ConditionalRender>
        </>
    );
}

export default App;
